import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useConfig } from '../../../../contexts/ConfigContext';
import { useStore } from '../../StoreContext';
import util from "@utils/utils";
import { MOMENT_FOREVER_DAYS, DATE_FORMAT_STRING } from '@constants'
import { FileCard } from '../FileCard';
import { LoadingDiv } from '@components/LoadingDiv';

import { Activity, ChevronRight, Star, Printer, Download, Grid, Menu, ChevronsUp, ChevronsDown, Database, Search as SearchIcon } from 'react-feather';
import { LoadingCardDiv } from '../LoadingCardDiv';
import { all } from 'axios';
import { PolicyChangeAnalysis } from '../PolicyChangeAnalysis';

import { Zap, Clock, FileText, Calendar, Download as DownloadIcon, Star as StarIcon, GitCompare, TrendingUp, Layers, Bookmark } from 'lucide-react';
import { MindTree } from '../MindTree';
import { Timeline } from '../Timeline';

import { MainSearchResults } from './MainSearchResults';
import { LeftMenu } from '../LeftMenu';

// 工具函数：清理文本，去除标点符号和空格
const cleanText = (text) => {
    return text?.replace(/[^\w\u4e00-\u9fa5]/g, '').toLowerCase() || '';
};

// 工具函数：清理政策标题，去除书名号、括号、发文号等
const cleanPolicyName = (name) => {
    if (!name) return '';

    return name
        // 去除书名号
        .replace(/[《》]/g, '')
        // 去除中英文括号及其内容
        .replace(/\([^)]*\)/g, '')
        .replace(/（[^）]*）/g, '')
        .replace(/\[[^\]]*\]/g, '')
        .replace(/【[^】]*】/g, '')
        // 去除常见发文号格式（如：国发〔2023〕12号、京政发[2023]5号等）
        .replace(/[京沪津渝川粤苏浙鲁豫湘鄂赣皖闽桂黔滇琼陕甘青宁新藏蒙辽吉黑国][^〔\[]*[〔\[][^〕\]]*[〕\]][^\d]*\d+号/g, '')
        // 去除其他常见发文号格式
        .replace(/\w+发\[[^\]]*\]\d+号/g, '')
        .replace(/\w+〔\d+〕\d+号/g, '')
        // 去除空格和特殊字符
        .replace(/\s+/g, '')
        .replace(/[^\w\u4e00-\u9fa5]/g, '')
        .toLowerCase()
        .trim();
};

// 工具函数：分组相同内容的政策
const groupSimilarPolicies = (data) => {
    if (!data || data.length === 0) return [];

    const result = [];
    const processedIndices = new Set();

    for (let i = 0; i < data.length; i++) {
        if (processedIndices.has(i)) continue;

        const currentItem = data[i];
        const currentCleanContent = cleanText(currentItem.related_content);
        const currentCleanTitle = cleanPolicyName(currentItem.policy_name);
        const group = [currentItem];

        // 查找相邻的相同内容或相同标题
        for (let j = i + 1; j < data.length; j++) {
            if (processedIndices.has(j)) break;

            const compareItem = data[j];
            const compareCleanContent = cleanText(compareItem.related_content);
            const compareCleanTitle = cleanPolicyName(compareItem.policy_name);

            // 判断内容相同或标题相同（且都不为空）
            const contentMatch = currentCleanContent === compareCleanContent && currentCleanContent.length > 0;
            const titleMatch = currentCleanTitle === compareCleanTitle && currentCleanTitle.length > 0;

            if (contentMatch || titleMatch) {
                group.push(compareItem);
                processedIndices.add(j);
            } else {
                break; // 只处理相邻的相同内容
            }
        }

        processedIndices.add(i);
        result.push({
            isGroup: group.length > 1,
            items: group,
            originalIndex: i
        });
    }

    return result;
};

// 新增快速搜索专用卡片组件


export const FileSearch = (props) => {
    const { clientId } = useConfig();
    const { state, actions } = useStore();
    const {
        fileName, setMdTxt, getPdfAction, getPdfLoading, setFileId, input, allData,
        loading, data, setQuery, setFileName,
        selectedCards, setSelectedCards, setMarkData, savedFoldListData, stateGridRelated, setStateGridRelated, searchK,
        searchOrg, // 新增
        filterTags, // 新增：筛选参数
        queryArgs, // 新增：完整的查询参数
        collapsed, // 新增：折叠状态
        setCollapsed, // 新增：设置折叠状态
        tab, // 新增：当前标签页
        setSearched, // 新增：设置搜索状态
        setDisplayQuery, // 新增：设置显示的查询参数
        statisticsData, // 新增：统计数据
        setSearchOrg, // 新增：设置搜索机构
        currentSection, // 新增：当前区域
        setCurrentSection // 新增：设置当前区域
    } = props
    const [mouseDown, setMouseDown] = useState(false);
    const [selecting, setSelecting] = useState(false);
    // const [upBtnVisible,setUpBtnVisible] = useState(false)
    const [viewMode, setViewMode] = useState('card')
    const [orderBy, setOrderBy] = useState('comprehensive')
    const [pageNum, setPageNum] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const prevSearchIndexRef = useRef('')
    const isSearchChanged = useRef(false)
    const [prevSearchK, setPrevSearchK] = useState('')

    // 新增：管理分组数据和折叠状态
    const [groupedData, setGroupedData] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState(new Set());



    // Replace collapse state with scroll navigation
    const topSectionRef = useRef(null);
    const mainSectionRef = useRef(null);

    // Add state for policy analysis collapse
    const [isPolicyAnalysisExpanded, setIsPolicyAnalysisExpanded] = useState(false);

    const pageNums = allData?.total_rows !== undefined && pageSize !== 0 ? Math.floor(allData?.total_rows / pageSize) + 1 : 1



    useEffect(() => {
        if (allData) {
            setPageNum(allData?.page_num)
            setPageSize(allData?.page_size)

        }
    }, [allData])

    useEffect(() => {
        setQuery({
            theme_id: '',
            search_content: ''
        })
    }, [])

    // 新增：处理数据分组
    useEffect(() => {
        if (data && data.length > 0) {
            const grouped = groupSimilarPolicies(data);
            setGroupedData(grouped);
            // 重置展开状态
            setExpandedGroups(new Set());
        }
    }, [data]);

    // Track search keyword changes
    useEffect(() => {
        if (searchK !== prevSearchK) {
            isSearchChanged.current = true
            setPrevSearchK(searchK)
        }
    }, [searchK, prevSearchK])

    // Track search index changes
    useEffect(() => {
        if (allData?.search_index && allData.search_index !== prevSearchIndexRef.current) {
            prevSearchIndexRef.current = allData.search_index
            isSearchChanged.current = false
        }
    }, [allData?.search_index])

    // Replace scroll-based collapse with scroll-based section detection
    useEffect(() => {
        const handleScroll = () => {
            // Since we removed the top section, always set current section to 'main'
            if (currentSection !== 'main') {
                setCurrentSection('main');
            }
        };

        // Use throttled scroll listener for better performance
        let ticking = false;
        const throttledScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', throttledScroll, { passive: true });
        
        return () => {
            window.removeEventListener('scroll', throttledScroll);
        };
    }, [currentSection, setCurrentSection]);

    // Smooth scroll to section function
    const scrollToSection = (sectionId) => {
        const targetRef = sectionId === 'top' ? topSectionRef : mainSectionRef;
        if (targetRef.current) {
            const headerHeight = document.querySelector('.sticky.top-0')?.offsetHeight || 0;
            const navHeight = document.querySelector('.scroll-nav-container')?.offsetHeight || 0;
            const elementTop = targetRef.current.offsetTop - headerHeight - navHeight - 20;
            
            // Add visual feedback before scrolling
            setCurrentSection(sectionId);
            
            window.scrollTo({
                top: Math.max(0, elementTop),
                behavior: 'smooth'
            });
            
            // Add a subtle highlight effect to the target section
            const targetElement = targetRef.current;
            if (targetElement) {
                targetElement.style.transition = 'box-shadow 0.3s ease-in-out';
                targetElement.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.3)';
                setTimeout(() => {
                    targetElement.style.boxShadow = '';
                }, 1000);
            }
        }
    };

    // useEffect(()=>{
    //     document.getElementById("fileSearchScrollDiv")?.addEventListener("scroll",e=>{
    //         util?.throttle(setUpBtnStatus(),1000)
    //     })
    // },[])

    // const setUpBtnStatus = () => {
    //     let scrollTop = document.getElementById("fileSearchScrollDiv")?.scrollTop
    //     // console.log(scrollTop)
    //     if(scrollTop > 500){
    //         setUpBtnVisible(true)
    //     }else{
    //         setUpBtnVisible(false)
    //     }
    // }

    const selectCardAction = (item) => {
        if (selectedCards?.some(i => i?.policy_id === item.source_id)) {
            setSelectedCards([...selectedCards.filter(i => i?.policy_id !== item.source_id)])
        } else {
            setSelectedCards([...selectedCards, { policy_id: item.source_id, policy_title: item.policy_name }])
        }
    }
    const selectAllAction = (e) => {
        if (e.target.checked) {
            setSelectedCards(data?.map(item => ({ policy_id: item.source_id, policy_title: item.policy_name })))
        } else {
            setSelectedCards([])
        }
    }

    const toggleGroupExpansion = (groupIndex) => {
        const newExpandedGroups = new Set(expandedGroups);
        if (newExpandedGroups.has(groupIndex)) {
            newExpandedGroups.delete(groupIndex);
        } else {
            newExpandedGroups.add(groupIndex);
        }
        setExpandedGroups(newExpandedGroups);
    };

    const renderFileCard = (item, index) => {
        return (
            <div className="card-body p-6 pb-4 flex-1" key={index}
                // onMouseDown={(e)=>{
                //     if (e.button === 0) {
                //         setMouseDown(true);
                //     }
                // }}
                // onMouseUp={(e)=>{
                //     if (e.button === 0) {
                //         setMouseDown(false);
                //     }
                // }}
                // onMouseMove={(e)=>{
                //     if(mouseDown){
                //         setSelecting(true)
                //     }
                // }}
                onClick={(e) => {
                    // if (selecting) {
                    //     e.preventDefault();
                    //     e.stopPropagation();
                    //     setSelecting(false);
                    // } else if(medium === 'qa' 
                    //     // && !collapsed
                    // ) {
                    selectCardAction(item)
                    // }
                }}
            >
                <FileCard
                    index={index}
                    pageNum={pageNum}
                    pageSize={pageSize}
                    item={item}
                    input={input}
                    getPdfAction={getPdfAction}
                    fileName={fileName}
                    setFileName={setFileName}
                    setFileId={setFileId}
                    setMarkData={setMarkData}
                    setMdTxt={setMdTxt}
                    savedFoldListData={savedFoldListData}
                    mindmapVisible
                    hightlight
                    score
                />
            </div>
        )
    }

    // 新增：渲染分组卡片
    const renderGroupedCard = (group, groupIndex, baseIndex) => {
        const { isGroup, items } = group;
        const isExpanded = expandedGroups.has(groupIndex);

        if (!isGroup) {
            // 单个项目，正常渲染
            return (
                <div key={groupIndex}>
                    <div className={` hover:shadow-lg card card-side bg-base-100 w-full card-bordered mb-4 ${selectedCards?.some(i => i?.policy_id === items[0]?.source_id) ? "border-primary border-2" : ''}`}>
                        <figure className=' w-12 mt-6  -mr-5 ml-2' style={{ alignItems: 'flex-start' }}>
                            <input type="checkbox" className="checkbox" checked={selectedCards?.some(i => i?.policy_id === items[0]?.source_id)} onClick={() => selectCardAction(items[0])} />
                        </figure>
                        {renderFileCard(items[0], baseIndex)}
                    </div>
                </div>
            );
        }

        // 分组项目，渲染第一个和折叠按钮
        return (
            <div key={groupIndex}>
                {/* 第一个项目 */}
                <div className={` hover:shadow-lg card card-side bg-base-100 w-full card-bordered mb-2 ${selectedCards?.some(i => i?.policy_id === items[0]?.source_id) ? "border-primary border-2" : ''}`}>
                    <figure className=' w-12 mt-6  -mr-5 ml-2' style={{ alignItems: 'flex-start' }}>
                        <input type="checkbox" className="checkbox" checked={selectedCards?.some(i => i?.policy_id === items[0]?.source_id)} onClick={() => selectCardAction(items[0])} />
                    </figure>
                    {renderFileCard(items[0], baseIndex)}
                </div>

                {/* 折叠按钮 */}
                <div className="mb-4">
                    <button
                        className="btn btn-link btn-sm w-full"
                        onClick={() => toggleGroupExpansion(groupIndex)}
                    >
                        {isExpanded ? (
                            <>
                                <ChevronsUp size="16" />
                                <span>收起相同政策</span>
                            </>
                        ) : (
                            <>
                                <ChevronsDown size="16" />
                                <span>展开不同来源的相同政策 ({items.length - 1}条)</span>
                            </>
                        )}
                    </button>
                </div>

                {/* 展开的其他项目 */}
                {isExpanded && items.slice(1).map((item, index) => (
                    <div key={`${groupIndex}-${index + 1}`} className="ml-4 mb-4">
                        <div className={` hover:shadow-lg card card-side bg-base-100 w-full card-bordered ${selectedCards?.some(i => i?.policy_id === item?.source_id) ? "border-primary border-2" : ''}`}>
                            <figure className=' w-12 mt-6  -mr-5 ml-2' style={{ alignItems: 'flex-start' }}>
                                <input type="checkbox" className="checkbox" checked={selectedCards?.some(i => i?.policy_id === item?.source_id)} onClick={() => selectCardAction(item)} />
                            </figure>
                            {renderFileCard(item, baseIndex + index + 1)}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // const searchResultDivScroll2Top = () => {
    //         let element = document.getElementById("fileSearchScrollDiv");
    //         element?.scrollTo({
    //             top: 0,
    //             behavior: "smooth"
    //         })
    // }
    // const Back2TopBtn = (
    //     <>
    //         {upBtnVisible && <div id="back-to-top" className="absolute bottom-20 -right-12 z-10">
    //             <button className="bg-base-100 group btn hover:btn-primary hover:text-white shadow-lg h-4" 
    //                 onClick={()=>searchResultDivScroll2Top()}
    //             >
    //                 <ChevronsUp size="1.5rem"/>
    //                 <span className='group-hover:block hidden'>回到顶部</span>
    //             </button>
    //         </div>}
    //     </>
    // )
    const BatchOperationDiv = () => {
        return (
            <div className='flex gap-6 items-center'>
                <div className="form-control">
                    <label className="label cursor-pointer">
                        <div className='flex items-center gap-4'>
                            <input type="checkbox" className="checkbox" onChange={(e) => selectAllAction(e)} />
                            <span className="label-text">全选</span>
                        </div>
                    </label>
                </div>
                <span>已选：{selectedCards?.length ?? 0}</span>
                <button className="btn btn-sm" disabled={selectedCards?.length === 0 || getPdfLoading}
                    onClick={() => getPdfAction({ policy_info: selectedCards })}
                >
                    {getPdfLoading && <span className="loading loading-spinner"></span>}
                    批量下载
                </button>
            </div>
        )
    }
    const BottomOperationDiv = () => {
        return (
            <div className='w-full bg-base-100 mb-2'>
                <div className='flex justify-between px-4 xl:px-8 items-center py-4'>
                    {BatchOperationDiv()}
                    <div className='flex gap-6 justify-end items-center'>
                        {PageOperationDiv()}
                    </div>
                </div>
            </div>

        )
    }
    const PageOperationDiv = () => {
        return (
            <div className="join">
                <button className="join-item btn btn-sm btn-out" disabled={pageNum == 1} onClick={() => setQuery({ page_num: pageNum - 1 })}>«</button>
                <button className="join-item btn btn-sm cursor-default">{`${pageNum} / ${pageNums}`}</button>
                <button className="join-item btn btn-sm" disabled={pageNum === pageNums} onClick={() => setQuery({ page_num: pageNum + 1 })}>»</button>
            </div>
        )
    }
    const ResultsFilterDiv = () => {
        return (
            <div className='w-full bg-base-100 mb-2 z-20 shadow'>
                <div className='border-b flex justify-between px-4 xl:px-8 py-4 items-center'>
                    <span className="flex gap-2 items-center">
                        为您找到{allData?.total_rows ?? 0}条相关政策

                    </span>
                    <div className='flex gap-6'>
                        {PageOperationDiv()}
                        <select className="select select-bordered select-sm" value={pageSize} onChange={(e) => { setPageSize(e.target.value); setQuery({ page_size: Number(e.target.value) }) }}>
                            <option value='10'>10条每页</option>
                            <option value='20'>20条每页</option>
                            <option value='50'>50条每页</option>
                        </select>

                    </div>
                </div>
                <div className='flex justify-between px-4 xl:px-8 items-center py-4 border-b'>
                    {BatchOperationDiv()}
                    <div className='flex gap-6 justify-end items-center'>
                        <div className="join">
                            <button
                                onClick={() => { setOrderBy('comprehensive'); setQuery({ order_by: 'comprehensive' }) }}
                                className={`btn join-item btn-sm ${orderBy === 'comprehensive' ? 'btn-active' : ''}`}>
                                综合检索
                            </button>
                            <button
                                onClick={() => { setOrderBy('relevance'); setQuery({ order_by: 'relevance' }) }}
                                className={`btn join-item btn-sm ${orderBy === 'relevance' ? 'btn-active' : ''}`}>
                                相关度 ↓
                            </button>
                            <button
                                onClick={() => { setOrderBy('time'); setQuery({ order_by: 'time' }) }}
                                className={`btn join-item btn-sm ${orderBy === 'time' ? 'btn-active' : ''}`}>
                                时效性 ↓
                            </button>
                        </div>

                        <div className="join">
                            <button
                                onClick={() => setViewMode('card')}
                                className={`btn join-item btn-sm ${viewMode === 'card' ? 'btn-active' : ''}`}>
                                <Grid size="16" />
                            </button>
                            <button
                                onClick={() => setViewMode('table')}
                                className={`btn join-item btn-sm ${viewMode === 'table' ? 'btn-active' : ''}`}>
                                <Menu size="16" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
    const TableDiv = (item, index) => {
        return (
            <tr>
                <td>
                    <label className="flex justify-center items-center">
                        <input type="checkbox" className="checkbox mr-2" checked={selectedCards?.some(i => i?.policy_id === item?.source_id)} onClick={() => selectCardAction(item)} />
                        {(pageNum - 1) * pageSize + index + 1}
                    </label>
                </td>
                {/* <>
                <td>
                    {item?.policy_name}
                </td>
                <td>
                    {item?.from_organization?.replaceAll(/:/g,"")}
                </td>
                <td>
                    {item?.policy_theme ? (
                        <div className="badge badge-outline inline-block mr-2">{item.policy_theme?.replaceAll(/:/g,"")}</div> 
                    ) : null
                )}
                </td>
                <td>
                    {util.dateTransformToCN(item?.policy_time)}
                </td>
                <td>
                    {(item?.score)?.toFixed(2)}
                </td>
                <td>
                    <div className="flex justify-center items-center">
                        <button className="btn btn-ghost btn-sm btn-secondary">
                            <Activity size="15"/>
                        </button>
                        <button className="btn btn-ghost btn-sm btn-secondary">
                            <ChevronRight size="15"/>
                        </button>
                        <button className="btn btn-ghost btn-sm btn-secondary">
                            <Download size="15"/>
                        </button>
                        <button className="btn btn-ghost btn-sm btn-secondary">
                            <StarIcon size="15"/>
                        </button>
                    </div>
                </td>
            </> */}
                <FileCard
                    getPdfAction={getPdfAction}
                    input={input}
                    type='row'
                    index={index}
                    item={item}
                    setFileId={setFileId}
                    fileName={fileName}
                    setFileName={setFileName}
                    setMarkData={setMarkData}
                    setMdTxt={setMdTxt}
                    savedFoldListData={savedFoldListData}
                    mindmapVisible
                    hightlight
                    score
                />

            </tr>

        )
    }



    return (
        <div className="w-full px-6 bg-base-200">
            {/* Policy Change Analysis - Collapsible */}
            <div className="w-full mb-6 bg-gradient-to-br from-blue-50/30 to-indigo-50/40 rounded-lg">
                <div className="p-4 border-b border-blue-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                <Database className="w-5 h-5 text-blue-600" />
                                <span className="font-semibold text-slate-700">政策变迁分析</span>
                            </div>
                            <div className="flex items-center gap-1 text-sm text-blue-600">
                                <TrendingUp className="w-4 h-4" />
                                <span>深度分析</span>
                            </div>
                            <span className="ml-2 px-2 py-0.5 rounded bg-blue-100 text-slate-700 border border-blue-200 text-xs">AI分析</span>
                        </div>
                        <button 
                            onClick={() => setIsPolicyAnalysisExpanded(!isPolicyAnalysisExpanded)}
                            className="btn btn-sm btn-ghost"
                        >
                            {isPolicyAnalysisExpanded ? (
                                <>
                                    <ChevronsUp size="16" />
                                    <span className="ml-1">收起</span>
                                </>
                            ) : (
                                <>
                                    <ChevronsDown size="16" />
                                    <span className="ml-1">展开</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
                
                {/* Collapsible Content */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isPolicyAnalysisExpanded ? 'max-h-200 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                    <div className="overflow-y-auto px-4 py-3 ">
                        {searchK ? (
                            <PolicyChangeAnalysis searchK={searchK} />
                        ) : (
                            <div className="text-center text-gray-400 py-8">输入关键词后显示分析结果</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Search Results Section with Left Menu */}
            {/* Header */}
            {clientId !== 'client-b' && (
                <div className="p-4 border-b border-teal-200">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <SearchIcon className="w-5 h-5 text-teal-600" />
                            <span className="font-semibold text-slate-700">关键词搜索结果</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-teal-600">
                            <Star className="w-4 h-4" />
                            <span>深度分析</span>
                        </div>
                        <span className="ml-2 px-2 py-0.5 rounded bg-teal-100 text-slate-700 border border-teal-200 text-xs">智能推荐</span>
                    </div>
                </div>
            )}
            <div 
                ref={mainSectionRef}
                id="main-section"
                className="w-full section-transition fade-in"
            >
                <div className='flex'>
                    {/* Left Menu - sticky */}
                    <div className='w-80 2xl:w-88 3xl:w-96 mr-4'>
                        <div className='sticky top-20 overflow-y-auto shadow-lg rounded-lg'>
                            <LeftMenu
                                loading={loading}
                                data={data} 
                                allData={allData}
                                setSearchOrg={setSearchOrg}
                                statisticsData={statisticsData}    
                                setQuery={setQuery}     
                                tab={tab}    
                                searchK={searchK}
                            />
                        </div>
                    </div>
                    
                    {/* Main Content Area */}
                    <div className='flex-1'>
                        <MainSearchResults
                            input={input}
                            getPdfAction={getPdfAction}
                            getPdfLoading={getPdfLoading}
                            loading={loading}
                            data={data}
                            setQuery={setQuery}
                            setSearched={setSearched}
                            setDisplayQuery={setDisplayQuery}
                            selectedCards={selectedCards}
                            setSelectedCards={setSelectedCards}
                            setMarkData={setMarkData}
                            savedFoldListData={savedFoldListData}
                            fileName={fileName}
                            setFileName={setFileName}
                            setFileId={setFileId}
                            setMdTxt={setMdTxt}
                            allData={allData}
                            stateGridRelated={stateGridRelated}
                            setStateGridRelated={setStateGridRelated}
                            searchK={searchK}
                            filterTags={queryArgs?.filter_tags}
                            queryArgs={queryArgs}
                            collapsed={collapsed}
                            setCollapsed={setCollapsed}
                            tab={tab}
                            state={state}
                            actions={actions}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}